import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupsIcon from "@mui/icons-material/Groups";
import MapIcon from "@mui/icons-material/Map";
import PublicIcon from "@mui/icons-material/Public";
import { Box, Button, Card, CardContent, CardMedia, Chip, Divider, Stack, Typography } from "@mui/material";
import { noop } from "lodash";
import { Link } from "react-router-dom";
import type { UserRole } from "../auth/auth";
import {
  storageUrlWithCredentials,
  useStorageUrlWithCredentials,
} from "../components/imagesPreview/storageUrlWithCredentials";
import { CardMediaImg } from "../publicPackages2/CardImage";
import { CardLinkArea } from "../publicPackages2/CardLinkArea";
import { packageCountries, packageGroupTypes } from "../publicPackages2/packageTagsInfo";
import { TransportationIcon, TransportationName } from "../publicPackages2/transportation";
import type { ECommerceOffer, ECommerceProduct } from "./ECommerceOffers";
import { BookOfferButton } from "./details/BookOfferButton";
import { useProductDateRangeLabel } from "./productDateRangeLabel";

interface Props {
  readonly value: ECommerceOffer;
  readonly role?: UserRole;
  readonly selectRoute: (value: ECommerceProduct[]) => void;
  readonly onContextMenu: () => void;
  readonly onMapClick: (url: string) => void;
}

export function ECommerceGridCard(props: Props) {
  const offer = props.value;
  const role = props.role;
  const isAdminOrAgent = role === "ADMIN" || role === "AGENT";
  const mapThumbnailUrl = useStorageUrlWithCredentials(offer.map?.thumbnail.url);
  return (
    <Card
      key={offer.id.value}
      onContextMenu={isAdminOrAgent ? props.onContextMenu : noop}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.05)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
        },
        "&:hover .trip-map-overlay": {
          opacity: 1,
        },
        "&:hover .trip-base-image": {
          transform: "scale(1.03)",
        },
      }}
    >
      <CardLinkArea
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
        to={`/e-commerce/${offer.id.value}`}
      >
        <Box sx={{ position: "relative" }}>
          <CardMediaImg
            height={200}
            imageUrl={offer.image?.thumbnail.url}
            className="trip-base-image"
            sx={{ transition: "transform 0.35s ease" }}
          />
          {mapThumbnailUrl && (
            <Box
              className="trip-map-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                transition: "opacity 0.25s ease",
                bgcolor: "rgba(0,0,0,0.18)",
              }}
            >
              <CardMedia
                component="img"
                style={{ backgroundColor: "lightgray" }}
                image={mapThumbnailUrl}
                height={200}
                alt={`Mapa ${offer.name}`}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.15))",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    storageUrlWithCredentials(offer.map?.www.url).then((url) => {
                      if (url) {
                        props.onMapClick(url);
                      }
                    });
                  }}
                  sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: "bold" }}
                >
                  Pokaż mapę
                </Button>
              </Box>
            </Box>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: 12, left: 12, right: 12, justifyContent: "space-between" }}
          >
            <Chip
              label={useProductDateRangeLabel(offer.products) || "Termin do ustalenia"}
              size="small"
              sx={{
                bgcolor: "rgba(0,0,0,0.65)",
                color: "#fff",
                fontWeight: 600,
                maxWidth: "calc(100% - 92px)",
                "& .MuiChip-label": { fontSize: "0.8rem" },
              }}
            />
            <Chip
              label={`ID: ${offer.id.userFriendly}`}
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.9)", fontWeight: "bold", flexShrink: 0 }}
            />
          </Stack>
        </Box>
        <CardContent sx={{ flexGrow: 1, p: 2, display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            {offer.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3,
              whiteSpace: "pre-line",
            }}
          >
            {offer.info}
          </Typography>

          {/* Tagi zastępujące długie listy */}
          <Stack spacing={2} sx={{ mb: 3, mt: "auto" }}>
            <Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <PublicIcon fontSize="small" sx={{ color: "text.disabled" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                  }}
                >
                  Kraje
                </Typography>
              </Stack>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {packageCountries(offer).map((c) => (
                  <Chip
                    key={c}
                    label={c}
                    size="small"
                    sx={{ bgcolor: "rgba(13, 110, 253, 0.08)", color: "primary.main", fontWeight: 500 }}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <GroupsIcon fontSize="small" sx={{ color: "text.disabled" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                  }}
                >
                  Dla kogo
                </Typography>
              </Stack>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {packageGroupTypes(offer).map((g) => (
                  <Chip key={g} label={g} size="small" variant="outlined" sx={{ borderColor: "rgba(0,0,0,0.12)" }} />
                ))}
              </Box>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Parametry i link do mapy */}
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                color: "text.secondary",
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: "center",
                }}
              >
                <AccessTimeIcon fontSize="small" />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "500",
                  }}
                >
                  {offer.days ?? "-"} dni
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: "center",
                }}
              >
                <TransportationIcon forPackage={offer} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "500",
                  }}
                >
                  <TransportationName forPackage={offer} />
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </CardLinkArea>

      <Stack direction="row" spacing={1} sx={{ p: 2, pt: 0 }}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          sx={{ py: 1, borderRadius: 2 }}
          component={Link}
          to={`/e-commerce/${offer.id.value}`}
        >
          Szczegóły
        </Button>
        <BookOfferButton
          variant="contained"
          color="primary"
          fullWidth
          sx={{ py: 1, borderRadius: 2 }}
          disableElevation
          offer={offer}
        ></BookOfferButton>

        <Button
          variant="outlined"
          onClick={() => props.selectRoute(offer.products)}
          title="Zobacz trasę wycieczki"
          sx={{ color: "text.secondary", borderColor: "rgba(0,0,0,0.15)", borderRadius: 2, minWidth: "48px", px: 0 }}
        >
          <MapIcon />
        </Button>
      </Stack>
    </Card>
  );
}
